import { useEffect, useState } from "react";
import { Button, Input, Modal, DialogFooter, Loader } from "@/ui/atoms";
import { FormField } from "@/ui/molecules";

export type TSegmentationRuleDraft = {
    account_code: string;
    is_mandatory_segment_2: boolean;
    is_mandatory_segment_4: boolean;
    is_mandatory_segment_5: boolean;
    is_mandatory_segment_6: boolean;
    is_mandatory_segment_7: boolean;
    has_custom_segment_2: boolean;
    has_custom_segment_5: boolean;
    has_custom_segment_6: boolean;
    has_custom_segment_7: boolean;
    is_active: boolean;
};

export type TSegmentationRuleFormModalProps = {
    open: boolean;
    mode: "create" | "edit";
    initial?: TSegmentationRuleDraft;
    isSaving?: boolean;
    error?: string | null;
    onSubmit: (draft: TSegmentationRuleDraft) => void;
    onClose: () => void;
};

const emptyDraft = (): TSegmentationRuleDraft => ({
    account_code: "",
    is_mandatory_segment_2: false,
    is_mandatory_segment_4: false,
    is_mandatory_segment_5: false,
    is_mandatory_segment_6: false,
    is_mandatory_segment_7: false,
    has_custom_segment_2: false,
    has_custom_segment_5: false,
    has_custom_segment_6: false,
    has_custom_segment_7: false,
    is_active: true,
});

const MANDATORY: { key: keyof TSegmentationRuleDraft; n: number }[] = [
    { key: "is_mandatory_segment_2", n: 2 },
    { key: "is_mandatory_segment_4", n: 4 },
    { key: "is_mandatory_segment_5", n: 5 },
    { key: "is_mandatory_segment_6", n: 6 },
    { key: "is_mandatory_segment_7", n: 7 },
];
const CUSTOM: { key: keyof TSegmentationRuleDraft; n: number }[] = [
    { key: "has_custom_segment_2", n: 2 },
    { key: "has_custom_segment_5", n: 5 },
    { key: "has_custom_segment_6", n: 6 },
    { key: "has_custom_segment_7", n: 7 },
];

export function SegmentationRuleFormModal({
    open,
    mode,
    initial,
    isSaving = false,
    error,
    onSubmit,
    onClose,
}: TSegmentationRuleFormModalProps) {
    const [draft, setDraft] = useState<TSegmentationRuleDraft>(initial ?? emptyDraft());

    // Re-seed the form whenever the modal (re)opens for a different row.
    useEffect(() => {
        if (open) setDraft(initial ?? emptyDraft());
    }, [open, initial]);

    const isValid = draft.account_code.trim() !== "";

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!isValid) return;
        onSubmit({ ...draft, account_code: draft.account_code.trim() });
    };

    const toggle = (key: keyof TSegmentationRuleDraft, checked: boolean) =>
        setDraft((prev) => ({ ...prev, [key]: checked }));

    return (
        <Modal
            open={open}
            onOpenChange={(next) => !next && onClose()}
            title={mode === "create" ? "Add Segmentation Rule" : "Edit Segmentation Rule"}
            description="Account, mandatory segments, custom segments, and status."
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                        {error}
                    </div>
                )}
                <FormField label="Account" htmlFor="rule-account" required>
                    <Input
                        id="rule-account"
                        aria-label="Account"
                        value={draft.account_code}
                        onChange={(e) => setDraft({ ...draft, account_code: e.target.value })}
                        placeholder="Account code"
                    />
                </FormField>
                <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">Mandatory segments</legend>
                    <div className="flex flex-wrap gap-3">
                        {MANDATORY.map(({ key, n }) => (
                            <label key={key} className="inline-flex items-center gap-1">
                                <input
                                    type="checkbox"
                                    aria-label={`Mandatory segment ${n}`}
                                    checked={Boolean(draft[key])}
                                    onChange={(e) => toggle(key, e.target.checked)}
                                />
                                <span className="text-xs">{n}</span>
                            </label>
                        ))}
                    </div>
                </fieldset>
                <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">Custom segments</legend>
                    <div className="flex flex-wrap gap-3">
                        {CUSTOM.map(({ key, n }) => (
                            <label key={key} className="inline-flex items-center gap-1">
                                <input
                                    type="checkbox"
                                    aria-label={`Custom segment ${n}`}
                                    checked={Boolean(draft[key])}
                                    onChange={(e) => toggle(key, e.target.checked)}
                                />
                                <span className="text-xs">{n}</span>
                            </label>
                        ))}
                    </div>
                </fieldset>
                <label className="inline-flex items-center gap-2">
                    <input
                        type="checkbox"
                        aria-label="Active"
                        checked={draft.is_active}
                        onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                    />
                    <span>Active</span>
                </label>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSaving || !isValid}>
                        {isSaving ? (
                            <>
                                <Loader size="sm" className="mr-2" />
                                Saving...
                            </>
                        ) : mode === "create" ? (
                            "Create"
                        ) : (
                            "Save"
                        )}
                    </Button>
                </DialogFooter>
            </form>
        </Modal>
    );
}
